/**
 * dryKeyboardMapping.js — all keyboard shortcuts for the app.
 *
 * Exports three things consumed by DryEditorClass and the XState machine:
 *   - keymaps       — user-facing shortcuts (GLOBAL + EDITOR + NOTE_TABS)
 *   - baseKeymaps   — low-level ProseMirror base bindings (Enter, Tab, etc.)
 *   - editorKeymap  — factory that converts a keymaps array into a ProseMirror keymap plugin
 *
 * Each entry shape:
 *   {
 *     commandName: string,         // unique ALL_CAPS identifier
 *     description: string,         // shown in Settings → Keyboard Shortcuts
 *     keyboardCommands: {
 *       mac: string[],             // e.g. ['command+b']
 *       windows: string[],
 *       linux: string[],
 *       default: string[],
 *     },
 *     method: (send, sendPalette) => void,  // send = appState.send
 *     region: string[],            // 'GLOBAL' | 'EDITOR' | 'NOTE_TABS'
 *   }
 *
 * Adding a new shortcut:
 *   1. Check the shortcut table in context/new-feature-checklist.md to avoid conflicts
 *   2. Add an entry to the `keymaps` array below
 *   3. If it only applies inside the ProseMirror editor, set region: ['EDITOR']
 *   4. If it applies everywhere, set region: ['GLOBAL', 'EDITOR']
 *
 * Registered shortcuts (all platforms use Ctrl; Mac also accepts Command where noted):
 *   Ctrl+N          NEW_NOTE            Create a new note
 *   Ctrl+W          CLOSE_NOTE          Close selected note
 *   Ctrl+M          MOVE_NOTEBOOK       Focus notebook selector
 *   Ctrl+Shift+M    VIEW_NOTEBOOK_PANEL Toggle notebook panel
 *   Ctrl+P          SET_PALETTE_VISIBILITY  Toggle command palette
 *   Ctrl+J          TOGGLE_QUICKSEARCH_VISIBILITY  Toggle quick search
 *   Ctrl+Shift+F    TOGGLE_NOTE_SEARCH  Toggle global fuzzy note search
 *   Ctrl+→          NEXT_TAB            Select next open note tab
 *   Ctrl+←          PREVIOUS_TAB        Select previous open note tab
 *   Ctrl+Shift+U    EDITOR_BULLETED_LIST
 *   Ctrl+Shift+O    EDITOR_ORDERED_LIST
 *   Tab             EDITOR_LIST_INDENT
 *   Shift+Tab       EDITOR_LIST_OUTDENT
 *   Ctrl+Z          EDITOR_UNDO
 *   Ctrl+Y          EDITOR_REDO
 *   Ctrl+B          EDITOR_BOLD
 *   Ctrl+U          EDITOR_UNDERLINE
 *   Ctrl+I          EDITOR_ITALIC
 *   Ctrl+H          EDITOR_HORIZONTAL_RULE
 */
import replace from 'lodash.replace';
// const replace = require('lodash.replace');
const { editorCommands } = require('./views/editor/editorCommands');
import { openLinkDialog } from './views/editor/LinkDialogContent';
import logger from './logger';
import constants from './constants';
const { EDITOR_COMMAND_NAME_MAP } = constants;

const createNewNote = (send) => {
  send({ type: 'CREATE_NOTE' });
};

const closeNote = (send) => {
  send({ type: 'CLOSE_NOTE' });
};

/**
 * Keyboard shortcuts
 * Important aspects of a keyboard shortcut
 * 1. What task is it doing
 * 2. description we want to give it to engineers and for logging
 * 3. Keyboard combinations to execute a command
 * 4. What method/function to execute
 * 5. Context where the keyboard shortcut is intended to be ran
 * # Needs to be sharable inside and outside of editor and other components
 * # Needs to be able to call editor commands withing requiring it to be passed in
 */

/*
Drynotes Keymap Schema:
  {
    commandName: 'COMMAND_NAME',
    description: 'description of task',
    keyboardCommands: {
      mac: ['tab'],
      windows: ['tab'],
      linux: ['tab'],
      all: [''],
    },
    method: () => {},
    region: 'EDITOR',
  },
*/

const keymaps = [
  {
    commandName: 'NEW_NOTE',
    description: 'create a new note',
    keyboardCommands: {
      mac: ['ctrl+n'],
      windows: ['ctrl+n'],
      linux: ['ctrl+n'],
      default: ['ctrl+n'],
    },
    method: createNewNote,
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'CLOSE_NOTE',
    description: 'close a selected note',
    keyboardCommands: {
      mac: ['ctrl+w'],
      windows: ['ctrl+w'],
      linux: ['ctrl+w'],
      default: ['ctrl+w'],
    },
    method: closeNote,
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'MOVE_NOTEBOOK',
    description: 'Move a note to a notebook',
    keyboardCommands: {
      mac: ['ctrl+m'],
      windows: ['ctrl+m'],
      linux: ['ctrl+m'],
      default: ['ctrl+m'],
    },
    method: () => {
      window.notebookSelectorForNoteRef.current.focus();
    },
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'VIEW_NOTEBOOK_PANEL',
    description: 'View Notebook Panel',
    keyboardCommands: {
      mac: ['ctrl+shift+m'],
      windows: ['ctrl+shift+m'],
      linux: ['ctrl+shift+m'],
      default: ['ctrl+shift+m'],
    },
    method: (send) => {
      send({ type: 'togglePanelVisability' });
    },
    region: ['GLOBAL'],
  },
  {
    commandName: 'SET_PALETTE_VISIBILITY',
    description: 'Toggle visibility of palette',
    keyboardCommands: {
      mac: ['ctrl+p'],
      windows: ['ctrl+p'],
      linux: ['ctrl+p'],
      default: ['ctrl+p'],
    },
    method: (send, sendPalette) => {
      send({ type: 'toggleCommandPalette' });
    },
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'TOGGLE_QUICKSEARCH_VISIBILITY',
    description: 'Toggle visibility of quicksearch',
    keyboardCommands: {
      mac: ['ctrl+j'],
      windows: ['ctrl+j'],
      linux: ['ctrl+j'],
      default: ['ctrl+j'],
    },
    method: (send) => {
      send({ type: 'toggleSearchVisibility' });
    },
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'TOGGLE_NOTE_SEARCH',
    description: 'Toggle global note fuzzy search',
    keyboardCommands: {
      mac: ['ctrl+shift+f'],
      windows: ['ctrl+shift+f'],
      linux: ['ctrl+shift+f'],
      default: ['ctrl+shift+f'],
    },
    method: (send) => {
      send({ type: 'toggleNoteSearch' });
    },
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'NEXT_TAB',
    description: 'Select the next tab on the notes list',
    keyboardCommands: {
      mac: ['option+right'],
      windows: ['ctrl+right'],
      linux: ['ctrl+right'],
      default: ['ctrl+right'],
    },
    method: (send) => {
      send({ type: 'selectNextNote' });
    },
    region: ['NOTE_TABS'],
  },
  {
    commandName: 'PREVIOUS_TAB',
    description: 'Select the previous tab on the notes list',
    keyboardCommands: {
      mac: ['option+left'],
      windows: ['ctrl+left'],
      linux: ['ctrl+left'],
      default: ['ctrl+left'],
    },
    method: (send) => {
      send({ type: 'selectPreviousNote' });
    },
    region: ['NOTE_TABS'],
  },
  {
    commandName: 'EDITOR_BULLETED_LIST',
    description: 'Toggle a bulleted list',
    keyboardCommands: {
      mac: ['command+shift+u'],
      windows: ['ctrl+shift+u'],
      linux: ['ctrl+shift+u'],
      default: ['ctrl+shift+u'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.unordered_list],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_ORDERED_LIST',
    description: 'Toggle a oredered list',
    keyboardCommands: {
      mac: ['command+shift+o'],
      windows: ['ctrl+shift+o'],
      linux: ['ctrl+shift+o'],
      default: ['ctrl+shift+o'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.ordered_list],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_LIST_INDENT',
    description: 'Indent a list',
    keyboardCommands: {
      mac: ['Tab'],
      windows: ['Tab'],
      linux: ['Tab'],
      default: ['Tab'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.indent_list],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_LIST_OUTDENT',
    description: 'Outdent a list',
    keyboardCommands: {
      mac: ['Shift+Tab'],
      windows: ['Shift+Tab'],
      linux: ['Shift+Tab'],
      default: ['Shift+Tab'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.outdent_list],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_UNDO',
    description: 'Undo editor history',
    keyboardCommands: {
      mac: ['command+z'],
      windows: ['ctrl+z'],
      linux: ['ctrl+z'],
      default: ['ctrl+z'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.undo],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_REDO',
    description: 'Redo editor history',
    keyboardCommands: {
      mac: ['command+y'],
      windows: ['ctrl+y'],
      linux: ['ctrl+y'],
      default: ['ctrl+y'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.redo],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_BOLD',
    description: 'Bold text',
    keyboardCommands: {
      mac: ['command+b'],
      windows: ['ctrl+b'],
      linux: ['ctrl+b'],
      default: ['ctrl+b'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.bold],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_UNDERLINE',
    description: 'Toggle an underline under your text',
    keyboardCommands: {
      mac: ['command+u'],
      windows: ['ctrl+u'],
      linux: ['ctrl+u'],
      default: ['ctrl+u'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.underline],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_ITALIC',
    description: 'Toggle an italic pn your text',
    keyboardCommands: {
      mac: ['command+i'],
      windows: ['ctrl+i'],
      linux: ['ctrl+i'],
      default: ['ctrl+i'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.italic],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_HORIZONTAL_RULE',
    description: 'Create a horizontal rule',
    keyboardCommands: {
      mac: ['ctrl+h'],
      windows: ['ctrl+h'],
      linux: ['ctrl+h'],
      default: ['ctrl+h'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.horizontal_rule],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_ENTER_NEW_LIST_ITEM',
    description: 'Create new list item on enter',
    keyboardCommands: {
      mac: ['Enter'],
      windows: ['Enter'],
      linux: ['Enter'],
      default: ['Enter'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.enter_list_item],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_ENTER_CHECK_LIST_ITEM',
    description: 'Create new check list item on enter',
    keyboardCommands: {
      mac: ['Enter'],
      windows: ['Enter'],
      linux: ['Enter'],
      default: ['Enter'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.enter_check_list_item],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_CHECK_LIST',
    description: 'Toggle a checklist',
    keyboardCommands: {
      mac: ['ctrl+t'],
      windows: ['ctrl+t'],
      linux: ['ctrl+t'],
      default: ['ctrl+t'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.check_list],
    region: ['EDITOR'],
  },
  {
    commandName: 'FOCUS_OPEN_NOTES_SEARCH',
    description: 'Focus the search input for open notes',
    keyboardCommands: {
      mac: ['command+shift+f'],
      windows: ['ctrl+shift+f'],
      linux: ['ctrl+shift+f'],
      default: ['ctrl+shift+f'],
    },
    method: () => {
      document.getElementById('open-notes-search-input')?.focus();
    },
    region: ['GLOBAL', 'EDITOR'],
  },
  {
    commandName: 'EDITOR_LINK',
    description: 'Insert or edit a link',
    keyboardCommands: {
      mac: ['command+k'],
      windows: ['ctrl+k'],
      linux: ['ctrl+k'],
      default: ['ctrl+k'],
    },
    method: (send, sendPalette, sendModal) => {
      if (sendModal) openLinkDialog(sendModal);
    },
    region: ['EDITOR'],
  },
];

const baseKeymaps = [
  {
    commandName: 'EDITOR_ENTER',
    description: 'Editor new line',
    keyboardCommands: {
      mac: ['Enter'],
      windows: ['Enter'],
      linux: ['Enter'],
      default: ['Enter'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.enter],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_EXIT_CODE',
    description: 'Editor exit code',
    keyboardCommands: {
      mac: ['command+Enter'],
      windows: ['ctrl+Enter'],
      linux: ['ctrl+Enter'],
      default: ['ctrl+Enter'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.exitCode],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_BACKSPACE',
    description: 'Editor backspace',
    keyboardCommands: {
      mac: ['Backspace'],
      windows: ['Backspace'],
      linux: ['Backspace'],
      default: ['Backspace'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.backspace],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_MOD_BACKSPACE',
    description: 'Editor mod backspace',
    keyboardCommands: {
      mac: ['command+Backspace'],
      windows: ['ctrl+Backspace'],
      linux: ['ctrl+Backspace'],
      default: ['ctrl+Backspace'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.mod_backspace],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_DELETE',
    description: 'Editor Delete',
    keyboardCommands: {
      mac: ['Delete'],
      windows: ['Delete'],
      linux: ['Delete'],
      default: ['Delete'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.delete],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_MOD_DELETE',
    description: 'Editor mod delete',
    keyboardCommands: {
      mac: ['command+Delete'],
      windows: ['ctrl+Delete'],
      linux: ['ctrl+Delete'],
      default: ['ctrl+Delete'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.mod_delete],
    region: ['EDITOR'],
  },
  {
    commandName: 'EDITOR_SELECT_ALL',
    description: 'Editor select all',
    keyboardCommands: {
      mac: ['command+a'],
      windows: ['ctrl+a'],
      linux: ['ctrl+a'],
      default: ['ctrl+a'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.select_all],
    region: ['EDITOR'],
  },
  {
    commandName: 'PALLET_CLOSE',
    description: 'Close palette window',
    keyboardCommands: {
      mac: ['esc'],
      windows: ['esc'],
      linux: ['esc'],
      default: ['esc'],
    },
    method: editorCommands[EDITOR_COMMAND_NAME_MAP.select_all],
    region: ['PALLETTE'],
  },
];

const editorKeymapConverter = (command) => {
  let newCommand = replace(command, /\+/g, '-');
  newCommand = replace(newCommand, /command/g, 'cmd');
  return newCommand;
};

/**
 * Format
 * JS Object
 * https://www.npmjs.com/package/react-shortcuts
 * https://craig.is/killing/mice
 */
const globalKeymaps = (keymaps = []) => {
  // return the keymaps that are used globally
  /*
{
  commandName: 'COMMAND_TASK',
  description: 'description of task',
  keyboardCommands: {
    osx: ['tab'],
    windows: ['tab'],
    linux: ['tab'],
    all: [''],
  },
  method: () => {},
  region: 'EDITOR',
}
*/
  let reactShortcuts = {};
  // Create a react-shortcuts from Drynotes Keymap Schema
  for (let index = 0; index < keymaps.length; index++) {
    const element = keymaps[index];
    const { commandName, description, keyboardCommands, method, region } =
      element;

    let reactCommands = { ...keyboardCommands };

    // Remove the default mapping
    delete reactCommands.default;

    // Convert Mac keyboard command to OSX
    reactCommands.osx = reactCommands.mac;
    delete reactCommands.mac;

    for (let index = 0; index < region.length; index++) {
      const elementRegion = region[index];
      reactShortcuts[elementRegion] = {
        ...reactShortcuts[elementRegion],
        [commandName]: {
          ...reactCommands,
        },
      };
    }

    // Very chatty, used for debugging only
    // logger.debug(`test ${JSON.stringify(reactShortcuts)}`);
  }
  return reactShortcuts;
};

/*
Schema 1:
{
  region: {
    commandName: 'keyboard shortcut' // string when supporting a single command for multiple platforms
  }
}

Schema 2:
{
  region: {
    commandName: { // object when supporting multiple platforms
      osx: ['command+enter'], // Multiple commands
      windows: 'enter', // single command
      linux: 'enter',
    }
  }
}

Example 1:
{
  MODAL: {
    ESCAPE: 'esc',
    ENTER: {
      default: 'enter',
      osx: ['command+enter'],
      windows: 'enter',
      linux: 'enter',
    },
  },
}
*/

/**
 * format
 * JS Object
 * https://github.com/ProseMirror/prosemirror-keymap
 */

/*
Schema:
{
  'keyboard shortcuts'
  method
}

Example 1:
{ 'Mod-z': undo }
*/
const editorKeymap = (keymapsToUse, globalServices) => {
  // return the keymaps that are used in the editor
  const mac =
    typeof navigator != 'undefined' ? /Mac/.test(navigator.platform) : false;
  let meta = false;
  let ctrl = false;

  if (mac) {
    meta = true;
  } else {
    ctrl = true;
  }

  let editorShortcuts = {};
  // Create a react-shortcuts from Drynotes Keymap Schema
  for (let index = 0; index < keymapsToUse.length; index++) {
    const element = keymapsToUse[index];
    const { commandName, keyboardCommands, method, region } = element;

    const platformKeyboardCommands = mac
      ? keyboardCommands.mac
      : keyboardCommands.default;

    // Modifiers can be given in any order. `Shift-` (or `s-`), `Alt-` (or
    // `a-`), `Ctrl-` (or `c-` or `Control-`) and `Cmd-` (or `m-` or
    // `Meta-`) are recognized. For characters that are created by holding
    // shift, the `Shift-` prefix is implied, and should not be added
    // explicitly.
    //
    // You can use `Mod-` as a shorthand for `Cmd-` on Mac and `Ctrl-` on
    // other platforms.
    // This library only supports a single platform at a time, where the global keymap supports multiple platforms.
    // So we will need to filter out multiple commands based on platform.
    // const mac = typeof navigator != "undefined" ? /Mac/.test(navigator.platform) : false
    // if (mac) meta = true; else ctrl = true

    const logInformation = () => {
      logger.debug(`element: `, element);
      logger.debug(`commandName: `, commandName);
      logger.debug(`keyboardCommands: `, keyboardCommands);
      logger.debug(`mac: ${mac}`);
    };

    // logInformation();

    if (region.includes('EDITOR')) {
      const keyboardCommand = editorKeymapConverter(platformKeyboardCommands);
      // Pass the same (send, sendPalette) args that getHotkeysByRegion provides,
      // extracted from globalServices so state-sending commands (e.g. palette toggle) work.
      const newHandler = () =>
        method(globalServices?.appState?.send, globalServices?.paletteState?.send, globalServices?.modalState?.send);
      if (editorShortcuts[keyboardCommand]) {
        // Chain: try the previously-registered handler first; fall through to this one if it returns falsy.
        const existing = editorShortcuts[keyboardCommand];
        editorShortcuts[keyboardCommand] = () => existing() || newHandler();
      } else {
        editorShortcuts[keyboardCommand] = newHandler;
      }
    }

    // logger.debug(`test`, editorShortcuts);
  }
  return editorShortcuts;
};

const pullKeyboardShortcut = (commandName) => {
  let command = null;
  for (let index = 0; index < keymaps.length; index++) {
    const element = keymaps[index];
    if (element.commandName === commandName) {
      command = element;
    }
  }

  return command;
};
/**
 * WIP 1/2/21
 */
const handleGlobalShortcuts = (action) => {
  logger.info(action, 'Drynotes - handleGlobalShortcuts: action');
  switch (action) {
    default:
      break;
    case 'NEW_NOTE': {
      createNewNote();
      break;
    }
    case 'CLOSE_NOTE': {
      this.closeNote();
      break;
    }
    case 'MOVE_NOTEBOOK': {
      pullKeyboardShortcut('MOVE_NOTEBOOK').method();
      break;
    }
    case 'SET_PALETTE_VISIBILITY': {
      pullKeyboardShortcut('SET_PALETTE_VISIBILITY').method();
      break;
    }
    case 'TOGGLE_QUICKSEARCH_VISIBILITY': {
      pullKeyboardShortcut('TOGGLE_QUICKSEARCH_VISIBILITY').method();
      break;
    }
  }
};

// Handle raw events from React
const handleRawEvents = (event, context) => {
  if (event.ctrlKey === true && event.key === 'm') {
    window.notebookSelectorForNoteRef.current.focus();
  }

  if (event.ctrlKey === true && event.key === 'p') {
    // palette toggle handled via XState sendPalette({ type: 'TOGGLE' })
  }

  if (event.ctrlKey === true && event.key === 'j') {
    // store.dispatch(toggleQuickSearchVisibility());
  }

  if (context === 'quicksearch' && event.key === 'Escape') {
    // store.dispatch(closeQuickSearchVisibility());
  }

  if (context === 'quicksearch' && event.key === 'ArrowUp') {
    // store.dispatch(selectPreviousSearchResult());
  }

  if (context === 'quicksearch' && event.key === 'ArrowDown') {
    // store.dispatch(selectNextSearchResult());
  }

  if (context === 'quicksearch' && event.key === 'Enter') {
    // const currentState = getCurrentState();
    // const noteId = getSelectedSearchId(currentState);
    // store.dispatch(openNote(noteId));
    // store.dispatch(closeQuickSearchVisibility());
  }

  if (context === 'palette' && event.key === 'Escape') {
    // Palette closes via onMenuClose → handleMenuClose in Palette.js
  }
  return event;
};

// Idea!
// const DryNotesClientAPIActions = bindActionCreators(DryNotesClientAPIActions, dispatch)

const globalKeys = globalKeymaps(keymaps);

const keyMapObject = { keymaps };
export default keyMapObject;

export {
  globalKeys,
  handleGlobalShortcuts,
  handleRawEvents,
  editorKeymap,
  keymaps,
  baseKeymaps,
};

export const keyMap = {
  NOTES: {
    // ... existing code ...
    CLOSE_NOTE: 'alt+w',
    // NEW_NOTE: 'alt+n',
    // NEXT_NOTE: 'alt+j',
    // ... existing code ...
    //   },
    // },
    // NEXT_NOTE: {
    // ... existing code ...
    //   handler: (event) => {
    //     event.preventDefault();
    //     const newNoteStore = getCurrentState();
    //     const {
    //       notes,
    //       selectedNote,
    // ... existing code ...
    //   },
    // },
    // PREVIOUS_NOTE: {
    // ... existing code ...
    //   handler: (event) => {
    //     event.preventDefault();
    //     const closeNoteState = getCurrentState();
    //     const {
    //       notes,
    //       selectedNote,
    // ... existing code ...
    // COMMAND_PALETTE: {
    //   sequences: ['ctrl+p'],
    //   handler: () => {
    //     const palletState = getCurrentState();
    //     if (palletState.showCommandPalette) {
    //       store.dispatch(hideCommandPalette());
    //     } else {
    //       store.dispatch(showCommandPalette());
    //     }
    //   },
  },
};

const KEYBINDINGS_STORAGE_KEY = 'drynotes:keybindings';

export const getKeybindingOverrides = () => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(KEYBINDINGS_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

export const setKeybindingOverride = (commandName, keys) => {
  const overrides = getKeybindingOverrides();
  overrides[commandName] = keys;
  localStorage.setItem(KEYBINDINGS_STORAGE_KEY, JSON.stringify(overrides));
};

export const resetKeybindingOverride = (commandName) => {
  const overrides = getKeybindingOverrides();
  delete overrides[commandName];
  localStorage.setItem(KEYBINDINGS_STORAGE_KEY, JSON.stringify(overrides));
};

export const getHotkeysByRegion = (region, send, sendPalette) => {
  if (typeof window === 'undefined') {
    return [];
  }
  const platform = window.navigator.platform.toLowerCase();
  const isMac = /mac/.test(platform);
  const overrides = getKeybindingOverrides();

  return keymaps
    .filter((keymap) => keymap.region.includes(region))
    .map((keymap) => {
      const { keyboardCommands, method, commandName } = keymap;
      const override = overrides[commandName];
      const keys = override ||
        (isMac ? keyboardCommands.mac : keyboardCommands.windows) ||
        keyboardCommands.default;
      return {
        keys: keys.join(','),
        callback: () => method(send, sendPalette),
        commandName,
      };
    });
};
